/**
 * Formatting utilities for Poker Reborn
 */

/**
 * Format balance for display with currency symbol
 * < 1,000: $500
 * 1,000 - 999,999: $12,500
 * >= 1,000,000: $1.2M
 */
export function formatBalance(amount: number): string {
  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }
  return `$${amount.toLocaleString('en-US')}`;
}

/**
 * Format balance for compact display (mobile)
 * Abbreviates at lower threshold
 */
export function formatBalanceCompact(amount: number): string {
  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (amount >= 10_000) {
    return `$${(amount / 1_000).toFixed(1)}K`;
  }
  return `$${amount.toLocaleString('en-US')}`;
}

/**
 * Format gold for display with G prefix
 * < 1,000: G500
 * 1,000 - 999,999: G12,500
 * >= 1,000,000: G1.2M
 */
export function formatGold(amount: number): string {
  if (amount >= 1_000_000) {
    return `G${(amount / 1_000_000).toFixed(1)}M`;
  }
  return `G${amount.toLocaleString('en-US')}`;
}

/**
 * Format gold for compact display (mobile)
 */
export function formatGoldCompact(amount: number): string {
  if (amount >= 1_000_000) {
    return `G${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (amount >= 10_000) {
    return `G${(amount / 1_000).toFixed(1)}K`;
  }
  return `G${amount.toLocaleString('en-US')}`;
}

/**
 * Format tickets for display with T prefix
 * < 1,000: T5
 * 1,000 - 999,999: T12,500
 * >= 1,000,000: T1.2M
 */
export function formatTickets(amount: number): string {
  if (amount >= 1_000_000) {
    return `T${(amount / 1_000_000).toFixed(1)}M`;
  }
  return `T${amount.toLocaleString('en-US')}`;
}

/**
 * Format tickets for compact display (mobile)
 */
export function formatTicketsCompact(amount: number): string {
  if (amount >= 1_000_000) {
    return `T${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (amount >= 10_000) {
    return `T${(amount / 1_000).toFixed(1)}K`;
  }
  return `T${amount.toLocaleString('en-US')}`;
}

/**
 * Format a number as currency (chips)
 */
export function formatChips(amount: number, compact = false): string {
  if (compact) {
    return formatCompact(amount);
  }

  return amount.toLocaleString('en-US');
}

/**
 * Format a number in compact notation (1K, 1M, etc.)
 */
export function formatCompact(amount: number): string {
  if (amount >= 1_000_000_000) {
    return `${(amount / 1_000_000_000).toFixed(1)}B`;
  }
  if (amount >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (amount >= 1_000) {
    return `${(amount / 1_000).toFixed(1)}K`;
  }
  return amount.toString();
}

/**
 * Format stakes as string (e.g., "1/2", "5/10")
 */
export function formatStakes(smallBlind: number, bigBlind: number): string {
  return `${formatChips(smallBlind)}/${formatChips(bigBlind)}`;
}

/**
 * Format a card code for display
 */
export function formatCard(rank: string, suit: string): string {
  const rankDisplay = rank === 'T' ? '10' : rank;
  const suitDisplay = getSuitSymbol(suit);
  return `${rankDisplay}${suitDisplay}`;
}

/**
 * Get suit symbol
 */
export function getSuitSymbol(suit: string): string {
  switch (suit.toLowerCase()) {
    case 'h':
      return '♥';
    case 'd':
      return '♦';
    case 'c':
      return '♣';
    case 's':
      return '♠';
    default:
      return suit;
  }
}

/**
 * Get suit color
 */
export function getSuitColor(suit: string): number {
  switch (suit.toLowerCase()) {
    case 'h':
    case 'd':
      return 0xCC0000; // Red
    case 'c':
    case 's':
      return 0x000000; // Black
    default:
      return 0x000000;
  }
}

/**
 * Format time in MM:SS
 */
export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format time remaining (e.g., "5s", "1m 30s")
 */
export function formatTimeRemaining(seconds: number): string {
  if (seconds < 60) {
    return `${Math.ceil(seconds)}s`;
  }

  const mins = Math.floor(seconds / 60);
  const secs = Math.ceil(seconds % 60);

  if (secs === 0) {
    return `${mins}m`;
  }

  return `${mins}m ${secs}s`;
}

/**
 * Format player action for display
 */
export function formatAction(action: string, amount?: number): string {
  switch (action.toLowerCase()) {
    case 'fold':
      return 'Fold';
    case 'check':
      return 'Check';
    case 'call':
      return amount ? `Call ${formatChips(amount)}` : 'Call';
    case 'raise':
      return amount ? `Raise to ${formatChips(amount)}` : 'Raise';
    case 'allin':
      return 'All-In';
    case 'bet':
      return amount ? `Bet ${formatChips(amount)}` : 'Bet';
    default:
      return action;
  }
}

/**
 * Format hand rank name for display
 */
export function formatHandRank(rank: string): string {
  const names: Record<string, string> = {
    high_card: 'High Card',
    pair: 'Pair',
    two_pair: 'Two Pair',
    three_of_a_kind: 'Three of a Kind',
    straight: 'Straight',
    flush: 'Flush',
    full_house: 'Full House',
    four_of_a_kind: 'Four of a Kind',
    straight_flush: 'Straight Flush',
    royal_flush: 'Royal Flush',
  };

  return names[rank] || rank;
}

/**
 * Format position name
 */
export function formatPosition(position: string): string {
  const names: Record<string, string> = {
    btn: 'Button',
    sb: 'Small Blind',
    bb: 'Big Blind',
    utg: 'Under the Gun',
    mp: 'Middle Position',
    co: 'Cut-off',
  };

  return names[position.toLowerCase()] || position;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength - 3)}...`;
}

/**
 * Pluralize a word
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  if (count === 1) {
    return singular;
  }
  return plural || `${singular}s`;
}
